import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Zap, Shield, BarChart3, GitBranch, Layers, Lock, Download, ChevronLeft, ChevronRight, Activity, Cpu, FileText, RotateCcw, Key, Settings, TrendingUp, CheckCircle, XCircle, AlertTriangle, Eye, Server, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── CSS Dashboard Slide Components ────────────────────────────

const SlideWrapper = ({ children }) => (
  <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 100%)', borderRadius: '0.75rem', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', overflow: 'hidden', fontFamily: "'Inter', system-ui, sans-serif" }}>
    {children}
  </div>
);

const MiniBar = ({ pct, color = '#10b981', h = 8 }) => (
  <div style={{ width: '100%', height: h, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 1s ease' }} />
  </div>
);

const MiniGauge = ({ score, label, color = '#10b981', size = 56 }) => {
  const r = size / 2 - 4; const c = 2 * Math.PI * r; const o = c - (score / 100) * c;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1e293b" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4} strokeDasharray={c} strokeDashoffset={o} strokeLinecap="round" />
      </svg>
      <div style={{ marginTop: -size + 18, fontSize: '0.75rem', fontWeight: 700, color: '#e2e8f0', lineHeight: `${size - 12}px` }}>{score}%</div>
      {label && <div style={{ fontSize: '0.5rem', color: '#64748b', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>}
    </div>
  );
};

const Badge = ({ text, color = '#10b981' }) => (
  <span style={{ fontSize: '0.5625rem', fontWeight: 600, padding: '0.125rem 0.5rem', borderRadius: 9999, background: color + '18', color }}>{text}</span>
);

const SlidePlatform = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Engineering Intelligence</div>
      <Badge text="LIVE" color="#10b981" />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
      {[{ l: 'Projects', v: '47', c: '#3b82f6' }, { l: 'Builds', v: '312', c: '#10b981' }, { l: 'Confidence', v: '87%', c: '#f59e0b' }, { l: 'Agents', v: '7', c: '#8b5cf6' }].map((s, i) => (
        <div key={i} style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.625rem', textAlign: 'center', border: '1px solid #1e293b' }}>
          <div style={{ fontSize: '1.125rem', fontWeight: 700, color: s.c }}>{s.v}</div>
          <div style={{ fontSize: '0.5625rem', color: '#64748b', marginTop: 2 }}>{s.l}</div>
        </div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', flex: 1 }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Build Confidence Trend</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60 }}>
          {[65, 72, 68, 78, 82, 79, 85, 88, 91, 87, 90, 93].map((v, i) => (
            <div key={i} style={{ flex: 1, height: `${v}%`, background: v >= 80 ? '#10b981' : v >= 60 ? '#f59e0b' : '#ef4444', borderRadius: '2px 2px 0 0', opacity: 0.8 }} />
          ))}
        </div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Agent Activity</div>
        {['Product', 'Architect', 'Coding', 'Security'].map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: ['#3b82f6', '#8b5cf6', '#10b981', '#ef4444'][i] }} />
            <span style={{ fontSize: '0.5625rem', color: '#94a3b8', flex: 1 }}>{a}</span>
            <MiniBar pct={[92, 88, 95, 78][i]} color={['#3b82f6', '#8b5cf6', '#10b981', '#ef4444'][i]} h={4} />
          </div>
        ))}
      </div>
    </div>
  </SlideWrapper>
);

const SlideBuildPipeline = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Conversational Build Pipeline</div>
    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {['Idea', 'PRD', 'Architecture', 'Code', 'Test', 'Security', 'DevOps', 'Monitor', 'Done'].map((s, i) => (
        <React.Fragment key={i}>
          <div style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.5625rem', fontWeight: 600, background: i < 6 ? '#10b981' + '20' : '#1e293b', color: i < 6 ? '#10b981' : '#64748b', border: `1px solid ${i < 6 ? '#10b981' + '40' : '#1e293b'}` }}>{s}</div>
          {i < 8 && <ChevronRight size={10} style={{ color: '#334155' }} />}
        </React.Fragment>
      ))}
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b', flex: 1 }}>
      <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem' }}>Agent Chat</div>
      {[{ agent: 'Product Agent', msg: 'Generated PRD with 12 user stories', color: '#3b82f6' },
        { agent: 'Architect Agent', msg: 'React + FastAPI + PostgreSQL recommended', color: '#8b5cf6' },
        { agent: 'Coding Agent', msg: 'Generating 18 files (multi-pass)...', color: '#10b981' }].map((m, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', padding: '0.375rem', borderRadius: '0.25rem', background: '#1a2332' }}>
          <div style={{ width: 4, borderRadius: 2, background: m.color, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: m.color }}>{m.agent}</div>
            <div style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{m.msg}</div>
          </div>
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SlideRuntimeValidation = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Runtime Validation</div>
      <Badge text="GRADE: A" color="#10b981" />
    </div>
    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
      <MiniGauge score={88} label="Runtime" size={72} />
      <div style={{ flex: 1 }}>
        {[{ n: 'Health Endpoint', s: 'pass' }, { n: 'Env Variables', s: 'pass' }, { n: 'DB Connection', s: 'pass' }, { n: 'Error Handling', s: 'pass' }, { n: 'Entrypoint', s: 'pass' }, { n: 'Dependencies', s: 'warn' }, { n: 'Test Coverage', s: 'pass' }].map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
            {c.s === 'pass' ? <CheckCircle size={10} style={{ color: '#10b981' }} /> : <AlertTriangle size={10} style={{ color: '#f59e0b' }} />}
            <span style={{ fontSize: '0.5625rem', color: '#94a3b8', flex: 1 }}>{c.n}</span>
            <span style={{ fontSize: '0.5rem', fontWeight: 600, color: c.s === 'pass' ? '#10b981' : '#f59e0b', textTransform: 'uppercase' }}>{c.s}</span>
          </div>
        ))}
      </div>
    </div>
  </SlideWrapper>
);

const SlideDeployGates = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Deployment Gating Engine</div>
      <Badge text="APPROVED" color="#10b981" />
    </div>
    {[{ n: 'Build Confidence', v: '87%', t: '60%', s: true }, { n: 'Runtime Confidence', v: '88%', t: '50%', s: true }, { n: 'Security Review', v: '0 critical', t: '0', s: true }, { n: 'Policy Compliance', v: '0 violations', t: '0', s: true }, { n: 'Test Coverage', v: 'Present', t: 'Required', s: true }].map((g, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: '#0f172a', borderRadius: '0.375rem', border: '1px solid #1e293b' }}>
        <CheckCircle size={14} style={{ color: '#10b981' }} />
        <span style={{ fontSize: '0.625rem', fontWeight: 600, color: '#e2e8f0', flex: 1 }}>{g.n}</span>
        <span style={{ fontSize: '0.5625rem', color: '#10b981', fontWeight: 600 }}>{g.v}</span>
        <span style={{ fontSize: '0.5rem', color: '#64748b' }}>min: {g.t}</span>
      </div>
    ))}
  </SlideWrapper>
);

const SlideGovernance = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Org Governance Dashboard</div>
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'center' }}>
      <MiniGauge score={82} label="Health Index" color="#3b82f6" size={80} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
      {[{ l: 'Avg Build Confidence', v: '87%', c: '#10b981' }, { l: 'Retry Rate', v: '8%', c: '#f59e0b' }, { l: 'Security Posture', v: '94%', c: '#3b82f6' }, { l: 'Policy Violations', v: '0', c: '#10b981' }, { l: 'Monthly Cost', v: '$142', c: '#8b5cf6' }, { l: 'Builds (30d)', v: '47', c: '#3b82f6' }].map((s, i) => (
        <div key={i} style={{ background: '#0f172a', borderRadius: '0.375rem', padding: '0.5rem', border: '1px solid #1e293b', textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: s.c }}>{s.v}</div>
          <div style={{ fontSize: '0.5rem', color: '#64748b' }}>{s.l}</div>
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SlideAuditLog = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Full Audit Trail</div>
      <Badge text="EXPORTABLE" color="#3b82f6" />
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', border: '1px solid #1e293b', overflow: 'hidden', flex: 1 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '0.375rem 0.5rem', background: '#1e293b', fontSize: '0.5rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
        <span>Time</span><span>Action</span><span>Agent</span><span>Model</span>
      </div>
      {[{ t: '14:23:01', a: 'build_completed', ag: 'Orchestrator', m: 'claude-4' },
        { t: '14:22:45', a: 'code_generated', ag: 'CodingAgent', m: 'sonnet-4' },
        { t: '14:21:12', a: 'arch_designed', ag: 'ArchitectAgent', m: 'sonnet-4' },
        { t: '14:20:30', a: 'prd_generated', ag: 'ProductAgent', m: 'haiku-4.5' },
        { t: '14:19:55', a: 'checkpoint_approved', ag: 'User', m: '-' },
        { t: '14:18:00', a: 'build_started', ag: 'Orchestrator', m: '-' }].map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '0.375rem 0.5rem', borderTop: '1px solid #1e293b', fontSize: '0.5625rem' }}>
          <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{r.t}</span>
          <span style={{ color: r.a.includes('completed') ? '#10b981' : r.a.includes('fail') ? '#ef4444' : '#94a3b8' }}>{r.a}</span>
          <span style={{ color: '#8b5cf6' }}>{r.ag}</span>
          <span style={{ color: '#64748b' }}>{r.m}</span>
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SlideBuildReplay = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Deterministic Build Replay</div>
      <Badge text="FROZEN CONFIG" color="#8b5cf6" />
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b', fontFamily: 'monospace', fontSize: '0.5625rem', color: '#10b981', lineHeight: 1.7, flex: 1 }}>
      <span style={{ color: '#64748b' }}>{'{'}</span><br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"model"</span>: <span style={{ color: '#10b981' }}>"claude-sonnet-4"</span>,<br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"temperature"</span>: <span style={{ color: '#3b82f6' }}>0.3</span>,<br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"token_caps"</span>: <span style={{ color: '#64748b' }}>{'{'}</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"prd"</span>: <span style={{ color: '#3b82f6' }}>16000</span>,<br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"per_file"</span>: <span style={{ color: '#3b82f6' }}>64000</span><br />
      &nbsp;&nbsp;<span style={{ color: '#64748b' }}>{'}'}</span>,<br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"validation"</span>: <span style={{ color: '#64748b' }}>{'{'}</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"auto_repair"</span>: <span style={{ color: '#3b82f6' }}>true</span>,<br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>"structural_review"</span>: <span style={{ color: '#3b82f6' }}>true</span><br />
      &nbsp;&nbsp;<span style={{ color: '#64748b' }}>{'}'}</span><br />
      <span style={{ color: '#64748b' }}>{'}'}</span>
    </div>
  </SlideWrapper>
);

const SlideSecurity = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Security Intelligence</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', flex: 1 }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>OWASP Top 10</div>
        {['Injection', 'Broken Auth', 'XSS', 'Insecure Design', 'Misconfig'].map((v, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
            <CheckCircle size={10} style={{ color: '#10b981' }} />
            <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Findings</div>
        {[{ l: 'Critical', v: 0, c: '#ef4444' }, { l: 'High', v: 1, c: '#f59e0b' }, { l: 'Medium', v: 3, c: '#3b82f6' }, { l: 'Low', v: 7, c: '#64748b' }].map((f, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
            <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{f.l}</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: f.c }}>{f.v}</span>
          </div>
        ))}
      </div>
    </div>
  </SlideWrapper>
);

const SlideSecrets = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Secret Management</div>
      <Badge text="3 FOUND" color="#ef4444" />
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b', flex: 1 }}>
      {[{ f: 'config/db.js', t: 'Connection String', l: 12, sev: 'critical' },
        { f: 'src/api.js', t: 'API Key', l: 45, sev: 'high' },
        { f: '.env.example', t: 'JWT Secret', l: 3, sev: 'high' }].map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.375rem 0', borderBottom: i < 2 ? '1px solid #1e293b' : 'none' }}>
          <Lock size={10} style={{ color: s.sev === 'critical' ? '#ef4444' : '#f59e0b', marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#e2e8f0' }}>{s.t} in <code style={{ color: '#3b82f6' }}>{s.f}</code></div>
            <div style={{ fontSize: '0.5rem', color: '#64748b' }}>Line {s.l} | Replace with env var</div>
          </div>
          <Badge text={s.sev.toUpperCase()} color={s.sev === 'critical' ? '#ef4444' : '#f59e0b'} />
        </div>
      ))}
      <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#1a2332', borderRadius: '0.25rem', fontFamily: 'monospace', fontSize: '0.5rem', color: '#10b981' }}>
        # Generated .env template<br />
        DB_CONNECTION_STRING=REPLACE<br />
        API_KEY_0=REPLACE<br />
        JWT_SECRET_0=REPLACE
      </div>
    </div>
  </SlideWrapper>
);

const SlideCICD = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>CI/CD Sync Mode</div>
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
      {['GitHub Actions', 'GitLab CI', 'Bitbucket'].map((p, i) => (
        <div key={i} style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.5625rem', fontWeight: 600, background: i === 0 ? '#3b82f6' + '20' : '#1e293b', color: i === 0 ? '#3b82f6' : '#64748b', border: `1px solid ${i === 0 ? '#3b82f6' + '40' : '#1e293b'}` }}>{p}</div>
      ))}
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b', fontFamily: 'monospace', fontSize: '0.5rem', color: '#94a3b8', lineHeight: 1.8, flex: 1 }}>
      <span style={{ color: '#f59e0b' }}>name:</span> <span style={{ color: '#10b981' }}>CodeFoundry CI/CD</span><br />
      <span style={{ color: '#f59e0b' }}>on:</span> [push]<br />
      <span style={{ color: '#f59e0b' }}>jobs:</span><br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>confidence-gate:</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#64748b' }}># Injected by CodeFoundry</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>run:</span> <span style={{ color: '#10b981' }}>curl /confidence-score</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>if:</span> <span style={{ color: '#10b981' }}>score {'>'} 60%</span><br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>build:</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>needs:</span> confidence-gate<br />
      &nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>deploy:</span><br />
      &nbsp;&nbsp;&nbsp;&nbsp;<span style={{ color: '#f59e0b' }}>needs:</span> build
    </div>
  </SlideWrapper>
);

const SlideConfidenceAPI = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Engineering Confidence API</div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {[{ method: 'GET', path: '/confidence-score/{id}', desc: 'Build + runtime scores' },
        { method: 'GET', path: '/policy-report/{id}', desc: 'Policy compliance report' },
        { method: 'GET', path: '/runtime-health/{id}', desc: 'Runtime validation status' }].map((ep, i) => (
        <div key={i} style={{ background: '#0f172a', borderRadius: '0.375rem', padding: '0.625rem', border: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '0.5rem', fontWeight: 700, color: '#10b981', fontFamily: 'monospace', background: '#10b981' + '18', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{ep.method}</span>
            <span style={{ fontSize: '0.5625rem', fontFamily: 'monospace', color: '#3b82f6' }}>{ep.path}</span>
          </div>
          <div style={{ fontSize: '0.5rem', color: '#64748b' }}>{ep.desc}</div>
        </div>
      ))}
      <div style={{ background: '#0f172a', borderRadius: '0.375rem', padding: '0.625rem', border: '1px solid #3b82f6' + '30', marginTop: 'auto' }}>
        <div style={{ fontSize: '0.5625rem', color: '#3b82f6', fontWeight: 600 }}>Integrate into your stack</div>
        <div style={{ fontSize: '0.5rem', color: '#64748b' }}>Internal dashboards, change management, deployment pipelines</div>
      </div>
    </div>
  </SlideWrapper>
);

const SlideModelRouting = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Private Model Routing</div>
      <Badge text="ENTERPRISE" color="#8b5cf6" />
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {[{ agent: 'Product', provider: 'Anthropic', model: 'Claude Sonnet 4', c: '#3b82f6' },
        { agent: 'Architecture', provider: 'OpenAI', model: 'GPT-4o', c: '#10b981' },
        { agent: 'Coding', provider: 'Azure OpenAI', model: 'Private Endpoint', c: '#8b5cf6' },
        { agent: 'Testing', provider: 'Google', model: 'Gemini Pro', c: '#f59e0b' }].map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: '#0f172a', borderRadius: '0.375rem', border: '1px solid #1e293b' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: r.c }} />
          <span style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#e2e8f0', width: 70 }}>{r.agent}</span>
          <ChevronRight size={10} style={{ color: '#334155' }} />
          <span style={{ fontSize: '0.5625rem', color: r.c, flex: 1 }}>{r.provider}</span>
          <span style={{ fontSize: '0.5rem', color: '#64748b' }}>{r.model}</span>
        </div>
      ))}
      <div style={{ fontSize: '0.5625rem', color: '#64748b', marginTop: 'auto', padding: '0.5rem', background: '#1a2332', borderRadius: '0.25rem' }}>
        Bring-your-own-LLM: Anthropic, OpenAI, Azure, Gemini, Custom endpoints
      </div>
    </div>
  </SlideWrapper>
);

const SlideDevOps = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>DevOps Intelligence</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', flex: 1 }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Deployment Frequency</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>4.2/wk</div>
        <div style={{ fontSize: '0.5rem', color: '#10b981' }}>+23% vs last month</div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Lead Time</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6' }}>2.1h</div>
        <div style={{ fontSize: '0.5rem', color: '#10b981' }}>-18% improvement</div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>MTTR</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b' }}>45min</div>
        <div style={{ fontSize: '0.5rem', color: '#10b981' }}>Below 1hr target</div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Change Failure</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>3.2%</div>
        <div style={{ fontSize: '0.5rem', color: '#64748b' }}>Elite performance</div>
      </div>
    </div>
  </SlideWrapper>
);

const SlideTesting = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Testing Intelligence</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', flex: 1 }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Test Coverage</div>
        {['Line', 'Branch', 'Function', 'Statement'].map((t, i) => (
          <div key={i} style={{ marginBottom: '0.375rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.5rem', color: '#94a3b8', marginBottom: 2 }}>
              <span>{t}</span><span>{[94, 82, 91, 96][i]}%</span>
            </div>
            <MiniBar pct={[94, 82, 91, 96][i]} h={4} />
          </div>
        ))}
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Reliability</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981', marginBottom: '0.25rem' }}>95%</div>
        <div style={{ fontSize: '0.5rem', color: '#64748b', marginBottom: '0.5rem' }}>Test reliability score</div>
        <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Flaky Tests</div>
        {['auth.login.spec', 'payment.flow'].map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
            <AlertTriangle size={8} style={{ color: '#f59e0b' }} />
            <span style={{ fontSize: '0.5rem', color: '#f59e0b' }}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  </SlideWrapper>
);

const SlideTestConversion = () => (
  <SlideWrapper>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Manual-to-Auto Test Conversion</div>
      <Badge text="$10/RUN" color="#f59e0b" />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', flex: 1 }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.375rem' }}>Manual Test Case</div>
        <div style={{ fontSize: '0.5rem', color: '#94a3b8', lineHeight: 1.6 }}>
          1. Navigate to login page<br />
          2. Enter valid credentials<br />
          3. Click "Sign In"<br />
          4. Verify dashboard loads<br />
          5. Check welcome message
        </div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.5625rem', fontWeight: 600, color: '#10b981', marginBottom: '0.375rem' }}>Generated pytest</div>
        <div style={{ fontSize: '0.5rem', color: '#10b981', fontFamily: 'monospace', lineHeight: 1.6 }}>
          def test_login():<br />
          &nbsp;&nbsp;page.goto("/login")<br />
          &nbsp;&nbsp;page.fill("#email")<br />
          &nbsp;&nbsp;page.fill("#pass")<br />
          &nbsp;&nbsp;page.click("Sign In")<br />
          &nbsp;&nbsp;assert "Dashboard"
        </div>
      </div>
    </div>
  </SlideWrapper>
);

const SlideIntelligence = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Longitudinal Intelligence</div>
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.25rem' }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.5rem', color: '#64748b', textTransform: 'uppercase' }}>Regression Risk</div>
        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#10b981' }}>12% <span style={{ fontSize: '0.5rem', color: '#64748b' }}>Low</span></div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid #1e293b' }}>
        <div style={{ fontSize: '0.5rem', color: '#64748b', textTransform: 'uppercase' }}>Repair Rate</div>
        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f59e0b' }}>8% <span style={{ fontSize: '0.5rem', color: '#10b981' }}>-3%</span></div>
      </div>
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b', flex: 1 }}>
      <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Confidence Over Time</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
        {[62, 68, 71, 69, 75, 78, 82, 80, 85, 87, 83, 89, 91, 88, 93].map((v, i) => (
          <div key={i} style={{ flex: 1, height: `${v}%`, background: `linear-gradient(to top, #10b981, #3b82f6)`, borderRadius: '2px 2px 0 0', opacity: 0.7 + (i / 50) }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
        <span style={{ fontSize: '0.4375rem', color: '#64748b' }}>W1</span>
        <span style={{ fontSize: '0.4375rem', color: '#64748b' }}>W15</span>
      </div>
    </div>
  </SlideWrapper>
);

const SlideCheckpoints = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Interactive Checkpoints</div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #f59e0b' + '40' }}>
        <div style={{ fontSize: '0.625rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.375rem' }}>Checkpoint: Review Requirements</div>
        <div style={{ fontSize: '0.5rem', color: '#94a3b8', marginBottom: '0.5rem' }}>The Product Agent has generated requirements. Review and approve to continue.</div>
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          <div style={{ padding: '0.25rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.5rem', fontWeight: 600, background: '#10b981', color: 'white' }}>Approve & Continue</div>
          <div style={{ padding: '0.25rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.5rem', fontWeight: 600, border: '1px solid #f59e0b', color: '#f59e0b' }}>Request Changes</div>
        </div>
      </div>
      <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #8b5cf6' + '40' }}>
        <div style={{ fontSize: '0.625rem', fontWeight: 600, color: '#8b5cf6', marginBottom: '0.375rem' }}>Checkpoint: Review Architecture</div>
        <div style={{ fontSize: '0.5rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Tech stack: React + FastAPI + PostgreSQL. 14 components identified.</div>
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          <div style={{ padding: '0.25rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.5rem', fontWeight: 600, background: '#10b981', color: 'white' }}>Approve & Continue</div>
          <div style={{ padding: '0.25rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.5rem', fontWeight: 600, border: '1px solid #f59e0b', color: '#f59e0b' }}>Request Changes</div>
        </div>
      </div>
    </div>
  </SlideWrapper>
);

const SlidePricing = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0', textAlign: 'center' }}>Plans</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.5rem', flex: 1 }}>
      {[{ n: 'Builder', p: '$149', b: '4 builds', f: ['Core pipeline', 'Security scan', 'Semantic search'] },
        { n: 'Pro', p: '$399', b: '12 builds', f: ['Git export', 'Priority queue', 'Multi-round debug'] },
        { n: 'Team', p: '$999', b: '30 builds', f: ['Terraform export', 'Enterprise Mode', 'Audit logs'] },
        { n: 'Enterprise', p: 'Custom', b: 'Unlimited', f: ['Private LLM', 'SSO', 'SLA', 'Dedicated'] }].map((pl, i) => (
        <div key={i} style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.625rem', border: `1px solid ${i === 2 ? '#3b82f6' : '#1e293b'}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 600, color: i === 2 ? '#3b82f6' : '#94a3b8', marginBottom: '0.25rem' }}>{pl.n}</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>{pl.p}</div>
          <div style={{ fontSize: '0.4375rem', color: '#64748b', marginBottom: '0.375rem' }}>{pl.b}/mo</div>
          {pl.f.map((f, fi) => (
            <div key={fi} style={{ fontSize: '0.4375rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
              <CheckCircle size={7} style={{ color: '#10b981' }} /> {f}
            </div>
          ))}
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SlideBCI = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Build Confidence Index</div>
    <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', alignItems: 'center' }}>
      <MiniGauge score={87} label="BCI" color="#10b981" size={80} />
      <MiniGauge score={88} label="Runtime" color="#3b82f6" size={80} />
      <MiniGauge score={94} label="Security" color="#8b5cf6" size={80} />
    </div>
    <div style={{ background: '#0f172a', borderRadius: '0.5rem', padding: '0.75rem', border: '1px solid #1e293b' }}>
      <div style={{ fontSize: '0.625rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Per-File Confidence</div>
      {['src/App.tsx', 'api/server.py', 'db/models.py', 'tests/test_api.py'].map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.5rem', color: '#94a3b8', width: 90, fontFamily: 'monospace' }}>{f}</span>
          <MiniBar pct={[95, 88, 92, 85][i]} h={5} color={[95, 88, 92, 85][i] >= 90 ? '#10b981' : '#3b82f6'} />
          <span style={{ fontSize: '0.5rem', color: '#10b981', fontWeight: 600, width: 25, textAlign: 'right' }}>{[95, 88, 92, 85][i]}%</span>
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SlideModelTiering = () => (
  <SlideWrapper>
    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#e2e8f0' }}>Intelligent Model Tiering</div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ fontSize: '0.5625rem', color: '#64748b' }}>Premium models for creative tasks, fast models for structured work</div>
      {[{ agent: 'Product Agent', model: 'Haiku 4.5', tier: 'Fast', c: '#f59e0b', cost: '$0.02' },
        { agent: 'Architect Agent', model: 'Sonnet 4', tier: 'Premium', c: '#8b5cf6', cost: '$0.15' },
        { agent: 'Coding Agent', model: 'Sonnet 4', tier: 'Premium', c: '#8b5cf6', cost: '$0.45' },
        { agent: 'Testing Agent', model: 'Haiku 4.5', tier: 'Fast', c: '#f59e0b', cost: '$0.03' },
        { agent: 'Security Agent', model: 'Haiku 4.5', tier: 'Fast', c: '#f59e0b', cost: '$0.02' },
        { agent: 'DevOps Agent', model: 'Haiku 4.5', tier: 'Fast', c: '#f59e0b', cost: '$0.02' }].map((a, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', background: '#0f172a', borderRadius: '0.25rem', border: '1px solid #1e293b' }}>
          <span style={{ fontSize: '0.5625rem', color: '#e2e8f0', width: 85, fontWeight: 600 }}>{a.agent}</span>
          <Badge text={a.tier} color={a.c} />
          <span style={{ fontSize: '0.5rem', color: '#94a3b8', flex: 1, fontFamily: 'monospace' }}>{a.model}</span>
          <span style={{ fontSize: '0.5rem', color: '#10b981' }}>{a.cost}</span>
        </div>
      ))}
    </div>
  </SlideWrapper>
);

const SLIDES = [
  { component: SlidePlatform, label: 'Platform Overview', desc: 'Full-stack engineering intelligence — 7 AI agents, confidence scoring, governance' },
  { component: SlideBuildPipeline, label: 'Conversational Build', desc: '7 specialized agents collaborate in real-time: Product, Architect, Coding, Testing, Security, DevOps, Monitoring — with your approval at every milestone' },
  { component: SlideCheckpoints, label: 'Interactive Checkpoints', desc: 'Pause the pipeline at key stages — review, approve, or request changes before continuing' },
  { component: SlideBCI, label: 'Build Confidence Index', desc: 'Quantified code quality with per-file confidence scoring and multi-pass validation' },
  { component: SlideRuntimeValidation, label: 'Runtime Validation', desc: 'Simulates container startup — validates health endpoints, env vars, DB connections, error handling' },
  { component: SlideDeployGates, label: 'Deployment Gates', desc: '5 automated gates block deployment if confidence, security, or policy checks fail' },
  { component: SlideGovernance, label: 'Org Governance', desc: 'Engineering Health Index — CTO-level visibility across all projects' },
  { component: SlideAuditLog, label: 'Full Audit Trail', desc: 'Every agent invocation logged with model, tokens, retries — exportable for compliance' },
  { component: SlideSecurity, label: 'Security Intelligence', desc: 'OWASP Top 10, CVE scanning, secret detection, and risk scoring on every build' },
  { component: SlideSecrets, label: 'Secret Management', desc: 'Detects hardcoded secrets, suggests env var replacements, generates .env templates' },
  { component: SlideBuildReplay, label: 'Deterministic Replay', desc: 'Freeze model version, temperature, prompts, token caps — replay with identical config' },
  { component: SlideIntelligence, label: 'Longitudinal Intelligence', desc: 'Track confidence trends, repair rates, regression risk over time — predictive analytics' },
  { component: SlideDevOps, label: 'DevOps Intelligence', desc: 'DORA metrics: deployment frequency, lead time, MTTR, change failure rate' },
  { component: SlideTesting, label: 'Testing Intelligence', desc: 'Coverage analysis, flakiness detection, regression prediction, reliability scoring' },
  { component: SlideTestConversion, label: 'Test Conversion', desc: 'Upload manual test cases, get automated pytest/Playwright code — 1:1 side-by-side mapping' },
  { component: SlideCICD, label: 'CI/CD Sync', desc: 'Export pipeline configs for GitHub Actions, GitLab CI, Bitbucket — with confidence gates' },
  { component: SlideConfidenceAPI, label: 'Confidence API', desc: 'Public API endpoints for integration into internal dashboards and deployment pipelines' },
  { component: SlideModelRouting, label: 'Model Routing', desc: 'Enterprise BYOLLM — route each agent through your own LLM provider' },
  { component: SlideModelTiering, label: 'Smart Model Tiering', desc: 'Premium models for creative tasks, fast models for structured work — optimized cost' },
  { component: SlidePricing, label: 'Pricing Plans', desc: 'From $149/mo Builder to Enterprise custom — 50%+ margins across all tiers' },
];

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  const goToSlide = useCallback((idx) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % SLIDES.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + SLIDES.length) % SLIDES.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleLogin = () => {
    window.location.href = `${API_URL}/api/auth/login`;
  };

  const handleGitHubLogin = () => {
    window.location.href = `${API_URL}/api/auth/github`;
  };

  // Auth mode state
  const [authMode, setAuthMode] = useState('main'); // main, email-signup, email-login, phone, 2fa
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '', phone: '', otp: '', code: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [otpChannel, setOtpChannel] = useState('sms'); // 'sms' or 'whatsapp'
  const [otpSent, setOtpSent] = useState(false);

  const handleEmailSignup = async () => {
    setAuthLoading(true); setAuthMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/signup/email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email, password: authForm.password, name: authForm.name }),
      });
      const data = await res.json();
      if (res.ok) { setAuthMessage(data.message); setAuthMode('email-login'); }
      else setAuthMessage(data.detail || 'Signup failed');
    } catch { setAuthMessage('Network error'); }
    finally { setAuthLoading(false); }
  };

  const handleEmailLogin = async () => {
    setAuthLoading(true); setAuthMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login/email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email, password: authForm.password }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.requires_2fa) { setTempToken(data.temp_token); setAuthMode('2fa'); }
        else window.location.href = '/build';
      }
      else setAuthMessage(data.detail || 'Login failed');
    } catch { setAuthMessage('Network error'); }
    finally { setAuthLoading(false); }
  };

  const handleSendOTP = async () => {
    setAuthLoading(true); setAuthMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/phone/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: authForm.phone, channel: otpChannel }),
      });
      const data = await res.json();
      if (res.ok) { setAuthMessage(data.message || 'OTP sent'); setOtpSent(true); }
      else setAuthMessage(data.detail || 'Failed to send OTP');
    } catch { setAuthMessage('Network error'); }
    finally { setAuthLoading(false); }
  };

  const handleVerifyOTP = async () => {
    setAuthLoading(true); setAuthMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/phone/verify-otp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: authForm.phone, otp: authForm.otp }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.requires_2fa) { setTempToken(data.temp_token); setAuthMode('2fa'); }
        else window.location.href = '/build';
      }
      else setAuthMessage(data.detail || 'Invalid OTP');
    } catch { setAuthMessage('Network error'); }
    finally { setAuthLoading(false); }
  };

  const handleVerify2FA = async () => {
    setAuthLoading(true); setAuthMessage('');
    try {
      const res = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: authForm.code }),
      });
      const data = await res.json();
      if (res.ok) window.location.href = '/build';
      else setAuthMessage(data.detail || 'Invalid code');
    } catch { setAuthMessage('Network error'); }
    finally { setAuthLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '0.625rem 0.875rem', borderRadius: '0.375rem',
    border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))',
    color: 'hsl(var(--foreground))', fontSize: '0.875rem', outline: 'none',
  };

  const authBtnStyle = (primary = false) => ({
    width: '100%', padding: '0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
    fontSize: '0.875rem', fontWeight: 600, border: primary ? 'none' : '1px solid hsl(var(--border))',
    background: primary ? 'hsl(var(--accent))' : 'hsl(var(--card))',
    color: primary ? 'white' : 'hsl(var(--foreground))',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    opacity: authLoading ? 0.6 : 1,
  });

  const features = [
    { icon: Zap, title: 'Guided Builds', desc: 'AI agents design, code, test, and secure your app — with your approval at every milestone.' },
    { icon: Shield, title: 'Security Intelligence', desc: 'OWASP scanning, CVE detection, and continuous risk scoring on every build.' },
    { icon: BarChart3, title: 'Cost Intelligence', desc: 'Track token usage, infrastructure costs, and surface optimization opportunities.' },
    { icon: GitBranch, title: 'CI/CD Generation', desc: 'Auto-generate Dockerfiles, GitHub Actions workflows, and deploy configs.' },
    { icon: Layers, title: 'Build Versioning', desc: 'Version snapshots with full rollback capability and diff visualization.' },
    { icon: Lock, title: 'Policy Engine', desc: 'Enforce compliance rules and guardrails across all builds and deployments.' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'hsl(var(--background))' }} data-testid="login-page">
      {/* Hero */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3.5rem 1.5rem 1.75rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '660px' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, fontFamily: 'Space Grotesk, sans-serif',
            marginBottom: '0.625rem', letterSpacing: '-0.035em', lineHeight: 1.1,
            color: 'hsl(var(--foreground))',
          }}>
            Code<span style={{ color: 'hsl(var(--accent))' }}>Foundry</span><span style={{ fontWeight: 400, opacity: 0.35 }}>.sh</span>
          </h1>
          <h2 style={{
            fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 700, letterSpacing: '-0.025em',
            marginBottom: '0.875rem', lineHeight: 1.25, color: 'hsl(var(--foreground))',
          }} data-testid="hero-headline">
            Build. Analyze. Improve. <span style={{ color: 'hsl(var(--accent))' }}>With Your Approval.</span>
          </h2>
          <p style={{
            color: 'hsl(220 10% 36%)', fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            fontWeight: 450, marginBottom: '2rem', lineHeight: 1.65,
            maxWidth: '520px', margin: '0 auto 2rem',
          }} data-testid="hero-subheadline">
            The Engineering Intelligence Platform — designing, coding, and deploying your apps with deterministic AI agents.
          </p>
          {error && (
            <div style={{
              background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)',
              borderRadius: 'var(--radius)', padding: '0.75rem', marginBottom: '1rem',
              fontSize: '0.875rem', fontWeight: 500, color: 'hsl(var(--destructive))',
            }} data-testid="login-error">
              Sign in failed. Please try again.
            </div>
          )}
          {authMessage && (
            <div style={{
              background: authMessage.includes('fail') || authMessage.includes('Invalid') || authMessage.includes('error') ? 'hsl(var(--destructive) / 0.1)' : 'hsl(160 65% 34% / 0.1)',
              border: `1px solid ${authMessage.includes('fail') || authMessage.includes('Invalid') || authMessage.includes('error') ? 'hsl(var(--destructive) / 0.3)' : 'hsl(160 65% 34% / 0.3)'}`,
              borderRadius: 'var(--radius)', padding: '0.625rem', marginBottom: '0.75rem',
              fontSize: '0.8125rem', fontWeight: 500, maxWidth: '340px', margin: '0 auto 0.75rem',
            }} data-testid="auth-message">{authMessage}</div>
          )}

          {/* Main Auth Options */}
          {authMode === 'main' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '340px', margin: '0 auto' }}>
              <button onClick={handleLogin} data-testid="login-google-button" style={{ ...authBtnStyle(true), background: '#fff', color: '#333', border: '1px solid #ddd' }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Get Started with Google
              </button>
              <button onClick={handleGitHubLogin} data-testid="login-github-button" style={{ ...authBtnStyle(), background: '#24292e', color: '#fff', border: 'none' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                Continue with GitHub
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.25rem 0' }}>
                <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
                <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
              </div>
              <button onClick={() => { setAuthMode('email-login'); setAuthMessage(''); }} data-testid="login-email-button" style={authBtnStyle()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7"/></svg>
                Continue with Email
              </button>
              <button onClick={() => { setAuthMode('phone'); setAuthMessage(''); }} data-testid="login-phone-button" style={authBtnStyle()}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>
                Continue with Phone
              </button>
            </div>
          )}

          {/* Email Login */}
          {authMode === 'email-login' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '340px', margin: '0 auto' }}>
              <input data-testid="email-input" type="email" placeholder="Email address" style={inputStyle} value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} />
              <input data-testid="password-input" type="password" placeholder="Password" style={inputStyle} value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleEmailLogin()} />
              <button onClick={handleEmailLogin} disabled={authLoading} data-testid="email-login-submit" style={authBtnStyle(true)}>
                {authLoading ? 'Signing in...' : 'Sign In'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                <button onClick={() => { setAuthMode('email-signup'); setAuthMessage(''); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--accent))', cursor: 'pointer', fontWeight: 500 }}>Create account</button>
                <button onClick={async () => {
                  if (!authForm.email) { setAuthMessage('Enter your email first'); return; }
                  const res = await fetch(`${API_URL}/api/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: authForm.email }) });
                  const data = await res.json();
                  setAuthMessage(data.message);
                }} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.75rem' }}>Forgot password?</button>
              </div>
              <button onClick={() => { setAuthMode('main'); setAuthMessage(''); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.75rem', marginTop: '0.25rem' }}>Back to all options</button>
            </div>
          )}

          {/* Email Signup */}
          {authMode === 'email-signup' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '340px', margin: '0 auto' }}>
              <input data-testid="name-input" type="text" placeholder="Full name" style={inputStyle} value={authForm.name} onChange={e => setAuthForm(p => ({ ...p, name: e.target.value }))} />
              <input data-testid="signup-email-input" type="email" placeholder="Email address" style={inputStyle} value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} />
              <input data-testid="signup-password-input" type="password" placeholder="Password (min 8 characters)" style={inputStyle} value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleEmailSignup()} />
              <button onClick={handleEmailSignup} disabled={authLoading} data-testid="email-signup-submit" style={authBtnStyle(true)}>
                {authLoading ? 'Creating...' : 'Create Account'}
              </button>
              <button onClick={() => { setAuthMode('email-login'); setAuthMessage(''); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--accent))', cursor: 'pointer', fontSize: '0.75rem' }}>Already have an account? Sign in</button>
              <button onClick={() => { setAuthMode('main'); setAuthMessage(''); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.75rem' }}>Back to all options</button>
            </div>
          )}

          {/* Phone OTP */}
          {authMode === 'phone' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '340px', margin: '0 auto' }}>
              <input data-testid="phone-input" type="tel" placeholder="Phone number (+1...)" style={inputStyle} value={authForm.phone} onChange={e => setAuthForm(p => ({ ...p, phone: e.target.value }))} />
              {/* SMS / WhatsApp Toggle */}
              <div data-testid="otp-channel-toggle" style={{ display: 'flex', borderRadius: '0.375rem', overflow: 'hidden', border: '1px solid hsl(var(--border))' }}>
                <button
                  data-testid="channel-sms-btn"
                  onClick={() => setOtpChannel('sms')}
                  style={{
                    flex: 1, padding: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', border: 'none',
                    background: otpChannel === 'sms' ? 'hsl(var(--accent))' : 'hsl(var(--card))',
                    color: otpChannel === 'sms' ? 'white' : 'hsl(var(--muted-foreground))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  SMS
                </button>
                <button
                  data-testid="channel-whatsapp-btn"
                  onClick={() => setOtpChannel('whatsapp')}
                  style={{
                    flex: 1, padding: '0.5rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', border: 'none',
                    borderLeft: '1px solid hsl(var(--border))',
                    background: otpChannel === 'whatsapp' ? '#25D366' : 'hsl(var(--card))',
                    color: otpChannel === 'whatsapp' ? 'white' : 'hsl(var(--muted-foreground))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </button>
              </div>
              <button onClick={handleSendOTP} disabled={authLoading} data-testid="send-otp-button" style={authBtnStyle(true)}>
                {authLoading ? 'Sending...' : `Send Code via ${otpChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`}
              </button>
              {otpSent && (
                <>
                  <input data-testid="otp-input" type="text" placeholder="Enter 6-digit code" style={{ ...inputStyle, letterSpacing: '0.25em', textAlign: 'center', fontSize: '1.125rem' }} maxLength={6} value={authForm.otp} onChange={e => setAuthForm(p => ({ ...p, otp: e.target.value.replace(/\D/g, '') }))} onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()} />
                  <button onClick={handleVerifyOTP} disabled={authLoading || authForm.otp.length < 6} data-testid="verify-otp-button" style={{ ...authBtnStyle(true), opacity: authForm.otp.length < 6 ? 0.5 : 1 }}>
                    {authLoading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                </>
              )}
              <button onClick={() => { setAuthMode('main'); setAuthMessage(''); setOtpSent(false); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.75rem' }}>Back to all options</button>
            </div>
          )}

          {/* 2FA Verification */}
          {authMode === '2fa' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '340px', margin: '0 auto' }}>
              <div style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
                <Shield size={24} style={{ color: 'hsl(var(--accent))', margin: '0 auto 0.5rem' }} />
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>Two-Factor Authentication</div>
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Enter the code from your authenticator app</div>
              </div>
              <input data-testid="2fa-code-input" type="text" placeholder="6-digit code" style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem', fontFamily: 'monospace' }} maxLength={6} value={authForm.code} onChange={e => setAuthForm(p => ({ ...p, code: e.target.value.replace(/\D/g, '') }))} onKeyDown={e => e.key === 'Enter' && handleVerify2FA()} />
              <button onClick={handleVerify2FA} disabled={authLoading || authForm.code.length < 6} data-testid="verify-2fa-button" style={{ ...authBtnStyle(true), opacity: authForm.code.length < 6 ? 0.5 : 1 }}>
                {authLoading ? 'Verifying...' : 'Verify'}
              </button>
              <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>You can also use a backup code</div>
              <button onClick={() => { setAuthMode('main'); setAuthMessage(''); }} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.75rem' }}>Back to all options</button>
            </div>
          )}

          <p style={{ marginTop: '0.875rem', fontSize: '0.8125rem', fontWeight: 500, color: 'hsl(220 10% 48%)', maxWidth: '480px', margin: '0.875rem auto 0' }}>
            CodeFoundry turns ideas into production-ready systems — with your approval at every stage.
          </p>
          <p style={{ fontSize: '0.625rem', color: 'hsl(220 10% 60%)', marginTop: '0.5rem' }}>
            A <span style={{ fontWeight: 600 }}>FoundryAI Labs</span> product &middot; foundryailabs.io
          </p>
        </div>
      </div>

      {/* Feature Carousel */}
      <div style={{ padding: '1.25rem 1.5rem 1.5rem', maxWidth: '960px', margin: '0 auto', width: '100%' }} data-testid="feature-carousel">
        <div style={{
          borderRadius: '0.875rem', overflow: 'hidden', border: '1px solid hsl(var(--border))',
          boxShadow: '0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
          position: 'relative', background: '#080808',
        }}>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '3/2', overflow: 'hidden' }}>
            {SLIDES.map((slide, idx) => (
              <div
                key={idx}
                data-testid={`carousel-slide-${idx}`}
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  opacity: currentSlide === idx ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  pointerEvents: currentSlide === idx ? 'auto' : 'none',
                }}
              >
                <slide.component />
              </div>
            ))}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent 15%, rgba(0,0,0,0.92))',
              height: '35%', pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute', bottom: '1rem', left: '1.5rem', right: '1.5rem', zIndex: 2,
            }}>
              <div style={{
                fontSize: 'clamp(0.9375rem, 2.5vw, 1.125rem)', fontWeight: 700, color: '#fff',
                marginBottom: '0.25rem', letterSpacing: '-0.02em', lineHeight: 1.2,
                textShadow: '0 2px 12px rgba(0,0,0,0.9)',
              }}>
                {SLIDES[currentSlide].label}
              </div>
              <div style={{
                fontSize: 'clamp(0.6875rem, 1.8vw, 0.8125rem)', fontWeight: 500, color: 'rgba(255,255,255,0.88)',
                lineHeight: 1.5, textShadow: '0 1px 8px rgba(0,0,0,0.9)',
              }}>
                {SLIDES[currentSlide].desc}
              </div>
            </div>
            {/* Nav arrows */}
            <button onClick={prevSlide} data-testid="carousel-prev"
              style={{
                position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '50%', width: 38, height: 38, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', zIndex: 3, backdropFilter: 'blur(8px)',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            <button onClick={nextSlide} data-testid="carousel-next"
              style={{
                position: 'absolute', right: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '50%', width: 38, height: 38, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', zIndex: 3, backdropFilter: 'blur(8px)',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
          {/* Dots */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem',
            background: 'hsl(var(--card))', borderTop: '1px solid hsl(var(--border))',
          }}>
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                data-testid={`carousel-dot-${idx}`}
                style={{
                  width: currentSlide === idx ? 24 : 8, height: 8, borderRadius: 4,
                  border: 'none', cursor: 'pointer',
                  background: currentSlide === idx ? 'hsl(var(--accent))' : 'hsl(var(--border))',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* PDF Download */}
      <div style={{ padding: '0.5rem 1.5rem 1.75rem', maxWidth: '960px', margin: '0 auto', width: '100%', textAlign: 'center' }}>
        <a
          href={`${API_URL}/api/investor-deck`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary"
          style={{
            fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.625rem 1.25rem', letterSpacing: '-0.01em',
          }}
          data-testid="download-pitch-deck-link"
        >
          <Download size={15} strokeWidth={2.5} /> Download Pitch Deck PDF
        </a>
      </div>

      {/* Thesis / Manifesto Section */}
      <div data-testid="thesis-section" style={{
        padding: '3.5rem 1.5rem', maxWidth: '960px', margin: '0 auto', width: '100%',
      }}>
        {/* The Problem */}
        <div style={{ maxWidth: '680px', margin: '0 auto', textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
            color: 'hsl(var(--accent))', marginBottom: '1rem',
          }}>The Next Bottleneck</div>
          <h2 style={{
            fontSize: 'clamp(1.375rem, 4vw, 2rem)', fontWeight: 750, letterSpacing: '-0.03em',
            lineHeight: 1.2, marginBottom: '1.25rem', color: 'hsl(var(--foreground))',
          }}>
            The bottleneck in software isn't writing code.<br />
            <span style={{ color: 'hsl(var(--accent))' }}>It's governing AI-written code.</span>
          </h2>
          <p style={{
            fontSize: 'clamp(0.875rem, 2vw, 1rem)', fontWeight: 450, lineHeight: 1.7,
            color: 'hsl(220 10% 36%)',
          }}>
            AI has dramatically increased engineering velocity. Copilots write code. Agents generate services. LLMs scaffold full applications. But as AI accelerates output, <strong style={{ fontWeight: 650, color: 'hsl(var(--foreground))' }}>risk scales faster than velocity.</strong>
          </p>
        </div>

        {/* The Tension */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '0.5rem',
          marginBottom: '3rem', maxWidth: '520px', margin: '0 auto 3rem',
        }}>
          {[
            ['More code', 'more confidence'],
            ['Faster deploys', 'safer deploys'],
            ['Generated tests', 'validated systems'],
            ['CI/CD automation', 'engineering intelligence'],
          ].map(([left, right], i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
              padding: '0.5rem 0',
            }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'hsl(var(--foreground))' }}>{left}</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'hsl(var(--destructive, 0 84% 60%))' }}>&ne;</span>
              <span style={{ fontSize: '0.9375rem', fontWeight: 650, color: 'hsl(var(--accent))' }}>{right}</span>
            </div>
          ))}
        </div>

        {/* The Category */}
        <div style={{
          maxWidth: '680px', margin: '0 auto', textAlign: 'center', marginBottom: '3rem',
          padding: '2.5rem 2rem', borderRadius: '1rem',
          background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
            color: 'hsl(var(--accent))', marginBottom: '1rem',
          }}>A New Category</div>
          <h3 style={{
            fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 700, letterSpacing: '-0.025em',
            lineHeight: 1.25, marginBottom: '1rem', color: 'hsl(var(--foreground))',
          }}>
            Not another coding assistant.<br />
            Not another CI tool.<br />
            <span style={{ color: 'hsl(var(--accent))' }}>An Engineering Intelligence Platform.</span>
          </h3>
          <p style={{
            fontSize: '0.9375rem', fontWeight: 450, lineHeight: 1.7, color: 'hsl(220 10% 36%)',
          }}>
            A system that scores deployment confidence, predicts regression risk, converts manual tests into automated suites, enforces policy-as-code, quantifies security exposure, and provides executive-level engineering KPIs.
          </p>
        </div>

        {/* Platform Capabilities */}
        <div style={{ maxWidth: '740px', margin: '0 auto', marginBottom: '3rem' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '0.625rem',
          }}>
            {[
              { label: 'Score deployment confidence', desc: 'Before every release' },
              { label: 'Predict regression risk', desc: 'Impact radius analysis' },
              { label: 'Convert manual to automated tests', desc: 'Excel, CSV, PDF, Doc' },
              { label: 'Enforce policy-as-code', desc: 'Token limits, library guardrails' },
              { label: 'Quantify security exposure', desc: 'OWASP, CVE, secret scanning' },
              { label: 'Estimate infrastructure cost', desc: 'Before deployment' },
              { label: 'Provide executive KPIs', desc: 'DORA metrics, quality trends' },
              { label: 'Track build intelligence', desc: 'Diff view, complexity scoring' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.625rem 0.875rem',
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: 'hsl(var(--accent))',
                }} />
                <div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 650, color: 'hsl(var(--foreground))' }}>{item.label}</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 450, color: 'hsl(220 10% 45%)', marginLeft: '0.375rem' }}>— {item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* The Vision */}
        <div style={{
          maxWidth: '680px', margin: '0 auto', textAlign: 'center', marginBottom: '2.5rem',
          padding: '2rem 1.5rem',
          borderTop: '1px solid hsl(var(--border))', borderBottom: '1px solid hsl(var(--border))',
        }}>
          <h3 style={{
            fontSize: 'clamp(1.125rem, 3vw, 1.375rem)', fontWeight: 700, letterSpacing: '-0.02em',
            lineHeight: 1.3, marginBottom: '1rem', color: 'hsl(var(--foreground))',
          }}>
            When AI writes software, something must<br />verify, govern, and optimize it.
          </h3>
          <p style={{
            fontSize: '0.9375rem', fontWeight: 450, lineHeight: 1.7, color: 'hsl(220 10% 36%)',
            marginBottom: '1.25rem',
          }}>
            As AI becomes the primary producer of code, organizations will require an intelligence layer that <strong style={{ fontWeight: 650, color: 'hsl(var(--foreground))' }}>measures trust</strong>, <strong style={{ fontWeight: 650, color: 'hsl(var(--foreground))' }}>quantifies risk</strong>, <strong style={{ fontWeight: 650, color: 'hsl(var(--foreground))' }}>enforces governance</strong>, and provides <strong style={{ fontWeight: 650, color: 'hsl(var(--foreground))' }}>engineering confidence at scale</strong>.
          </p>
          <p style={{
            fontSize: '1rem', fontWeight: 650, lineHeight: 1.5, color: 'hsl(var(--foreground))',
            letterSpacing: '-0.01em',
          }}>
            The winners in AI won't just generate more code.<br />
            They will <span style={{ color: 'hsl(var(--accent))' }}>control and optimize AI-powered systems.</span>
          </p>
        </div>

        {/* The Future */}
        <div style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center', marginBottom: '1rem' }}>
          <p style={{
            fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.02em',
            color: 'hsl(var(--foreground))', marginBottom: '0.5rem',
          }}>
            The future of engineering won't be human vs AI.
          </p>
          <p style={{
            fontSize: 'clamp(1.25rem, 3.5vw, 1.625rem)', fontWeight: 750, lineHeight: 1.3,
            letterSpacing: '-0.025em',
          }}>
            It will be <span style={{ color: 'hsl(var(--accent))' }}>AI supervised by intelligence.</span>
          </p>
        </div>

        {/* end thesis */}
      </div>

      {/* Features Grid */}
      <div style={{ padding: '0 1.5rem 3.5rem', maxWidth: '960px', margin: '0 auto', width: '100%' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.125rem',
        }}>
          {features.map((f, i) => (
            <div
              key={i}
              onMouseEnter={() => setHoveredCard(i)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                padding: '1.375rem 1.5rem', borderRadius: '0.875rem',
                border: `1px solid ${hoveredCard === i ? 'hsl(var(--accent) / 0.35)' : 'hsl(var(--border))'}`,
                background: 'hsl(var(--card))',
                boxShadow: hoveredCard === i
                  ? '0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)'
                  : '0 1px 3px rgba(0,0,0,0.04)',
                transform: hoveredCard === i ? 'translateY(-2px)' : 'translateY(0)',
                transition: 'all 0.25s ease',
                cursor: 'default',
              }}
              data-testid={`feature-card-${i}`}
            >
              <f.icon
                size={20}
                strokeWidth={2.25}
                style={{
                  color: 'hsl(var(--accent))',
                  marginBottom: '0.75rem',
                  transition: 'transform 0.2s',
                  transform: hoveredCard === i ? 'scale(1.1)' : 'scale(1)',
                }}
              />
              <h3 style={{
                fontSize: '0.9375rem', fontWeight: 650, marginBottom: '0.375rem',
                letterSpacing: '-0.015em', lineHeight: 1.3,
                color: 'hsl(var(--foreground))',
              }}>{f.title}</h3>
              <p style={{
                fontSize: '0.8125rem', fontWeight: 450, lineHeight: 1.6,
                color: 'hsl(220 10% 38%)',
              }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
